import { useCallback, useContext } from 'react';
import type { UseMutationConfig } from 'react-relay';
import type { MutationParameters, PayloadError } from 'relay-runtime';

import InteractionContext, { type InteractionContextType } from '@atlaskit/interaction-context';
import {
	getActiveInteraction,
	type InteractionMetrics,
} from '@atlaskit/react-ufo/interaction-metrics';
import { addUFOCustomData } from '@atlaskit/react-ufo/custom-data';

import {
	transformApplication,
	transformEdition,
} from '@atlassian/jira-common-experience-tracking-analytics/src/index.tsx';
import { setInteractionError } from '@atlassian/ufo-set-interaction-error';
import type { FetchError } from '@atlassian/jira-fetch/src/index.tsx';
import { isFetchError } from '@atlassian/jira-fetch/src/utils/errors.tsx';
import {
	startCapturingExtensions,
	stopCapturingExtensions,
	getExtensions,
} from '@atlassian/relay-capture-extensions';
import {
	useApplication,
	useEdition,
	useProjectKey,
} from '@atlassian/jira-project-context-service/src/main.tsx';
import {
	useAnalyticsSource,
	useIssueKey,
} from '@atlassian/jira-issue-context-service/src/main.tsx';
import { isAbortableError } from '@atlassian/jira-fetch/src/utils/is-abortable-error.tsx';
import { sendExperienceAnalytics } from '@atlassian/jira-issue-view-analytics/src/index.tsx';
import { extractFirstErrorFromRelayError } from '@atlassian/jira-issue-view-layout-experience-tracking/src/field-experience-tracking/index.tsx';
import type {
	AdditionalEventAttributes,
	ExperienceDescription,
} from '@atlassian/jira-issue-view-analytics/src/controllers/send-experience-analytics/index.tsx';

type PayloadErrorWithExtensions = PayloadError & { extensions?: { statusCode?: number } };

type AGGError = {
	message: string;
	extensions?: {
		statusCode?: number;
	};
};

type AGGResponse = {
	success: boolean;
	errors?: AGGError[];
	jira: {
		[key: string]: {
			errors?: AGGError[];
		};
	};
};

type Extensions = {
	gateway: {
		trace_id: string;
	};
};

const isExtensions = (value: unknown): value is Extensions => {
	return (
		typeof value === 'object' &&
		value !== null &&
		'gateway' in value &&
		typeof value.gateway === 'object' &&
		value.gateway !== null &&
		'trace_id' in value.gateway &&
		typeof value.gateway.trace_id === 'string'
	);
};

export const isAGGResponse = (value: unknown): value is AGGResponse => {
	return (
		typeof value === 'object' &&
		value !== null &&
		(('jira' in value && typeof value.jira === 'object' && value.jira !== null) ||
			('errors' in value && typeof value.errors === 'object'))
	);
};

export const extractError = (response: AGGResponse) => {
	if (response.errors) {
		return response.errors;
	}

	for (const key in response.jira) {
		if (response.jira[key]) {
			const value = response.jira[key];
			if (value.errors) {
				return value.errors;
			}
		}
	}

	return [];
};

const stopCapturingAndGetTraceId = (mutationName: string) => {
	const [extensions] = getExtensions(mutationName);
	stopCapturingExtensions(mutationName);
	return isExtensions(extensions) ? extensions.gateway.trace_id : 'unknown';
};

const isAbortableStatusCode = (statusCode?: number) => {
	return statusCode && statusCode >= 400 && statusCode < 500;
};

const stopCapturingAndGetMetaData = (
	mutationName: string,
	response?: AGGResponse,
	error?: PayloadErrorWithExtensions[] | null,
) => {
	const traceId = stopCapturingAndGetTraceId(mutationName);
	const aggError = error?.[0] || (response ? extractError(response)[0] : undefined);
	const statusCode = aggError?.extensions?.statusCode;
	const errorMessage = aggError?.message;

	return { traceId, errorMessage, statusCode, aggError };
};

const maybeSendUFOErrorAndReleaseHold = (
	activeInteraction: InteractionMetrics | undefined,
	ufoHoldCleanup: ReturnType<InteractionContextType['hold']>,
	traceId: string,
	sendUFOEvent?: boolean,
	hasError?: boolean,
	statusCode?: number,
	errorMessage?: string,
) => {
	if (sendUFOEvent) {
		if (hasError && activeInteraction?.ufoName) {
			addUFOCustomData({
				traceId,
				statusCode,
			});

			setInteractionError(activeInteraction?.ufoName, {
				errorMessage: errorMessage || 'unknown',
				name: errorMessage || 'unknown',
			});
		}
		ufoHoldCleanup?.();
	}
};

export const useIssueExperienceMonitoring = () => {
	const issueKey = useIssueKey();
	const projectKey = useProjectKey(issueKey);
	const application = useApplication(projectKey, true);
	const edition = useEdition(projectKey, true);
	const analyticsSource = useAnalyticsSource();
	const context = useContext(InteractionContext);

	/**
	 * Enriches React Relay mutation commits with comprehensive experience monitoring and analytics.
	 *
	 * This function wraps Relay mutation configurations to automatically:
	 * - Capture gateway trace IDs for debugging
	 * - Send UFO (performance) events with error handling
	 * - Track experience analytics with success/failure states
	 * - Handle both AGG API responses and network errors
	 *
	 * @param commitArgs - The original React Relay mutation configuration
	 * @param mutationName - Unique identifier for the mutation (used for trace capture)
	 * @param options - Configuration object for monitoring behavior
	 * @param options.sendUFOEvent - Whether to send UFO performance events (default: false)
	 * @param options.additionalAttributes - Extra analytics attributes to include
	 * @param options.task - Experience name for analytics (string or function that takes success boolean)
	 * @param options.overrideAttributesCallback - !Use with CAUTION! Function to override entire analytics attributes based on error and success attributes
	 *   Called with (isSuccess, mandatoryAttributes, errorObjects) and should return additional attributes.
	 *   Use this to add context-specific data like field names, validation errors, or business logic results.
	 * @param shouldWrapWithAnalytics - Function to conditionally enable monitoring (default: () => true)
	 *
	 * @returns Enhanced configuration for Relay commit with monitoring wrapped around onCompleted/onError
	 *
	 * @example
	 * ```tsx
	 * const [commit] = useMutation(UpdateIssueMutation);
	 * const { enrichCommitWithIssueExperienceMonitoring } = useIssueExperienceMonitoring();
	 *
	 * const handleUpdate = () => {
	 *   commit(enrichCommitWithIssueExperienceMonitoring({
	 *     variables: { input: { issueId, status: 'Done' } },
	 *     onCompleted: (response) => console.log('Success!'),
	 *   }, 'updateIssueStatus', {
	 *     sendUFOEvent: true,
	 *     task: 'issue.update.status',
	 *     additionalAttributes: { statusType: 'Done' }
	 *   }));
	 * };
	 * ```
	 */
	const enrichCommitWithIssueExperienceMonitoring = useCallback(
		<T extends MutationParameters>(
			commitArgs: UseMutationConfig<T>,
			mutationName: string,
			{
				sendUFOEvent,
				additionalAttributes,
				task,
				overrideAttributesCallback,
			}: {
				sendUFOEvent?: boolean;
				additionalAttributes?: AdditionalEventAttributes;
				task?: string;
				/**
				 * Callback to customize analytics attributes based on mutation outcome.
				 *
				 * @param isSuccess - Whether the mutation succeeded (true) or failed (false)
				 * @param mandatoryAttributes - !IMPORTANT! Always pass this in to ensure all the base attributes are being passed in
				 * @param errorObjects - Error details when isSuccess is false, containing:
				 *   - aggError: Parsed AGG API error with statusCode and message
				 *   - networkError: Boolean indicating if this was a network-level error
				 *   - statusCode: HTTP status code from the error
				 *   - rawError: The original Error or FetchError object
				 *
				 * @returns Additional attributes to merge with the analytics event
				 *
				 * @example
				 * ```tsx
				 * overrideAttributesCallback: (isSuccess, attrs, errors) => ({
				 *   fieldName: 'status',
				 *   ...(isSuccess ? { newValue: 'Done' } : {
				 *     errorType: errors.aggError?.statusCode === 403 ? 'permission' : 'validation'
				 *   })
				 * })
				 * ```
				 */
				overrideAttributesCallback?: (
					isSuccess: boolean,
					mandatoryAttributes: AdditionalEventAttributes | undefined,
					errorObjects: {
						aggError?: {
							statusCode: unknown;
							message: string | undefined;
						} | null;
						networkError?: boolean;
						statusCode?: unknown;
						rawError?: Error | FetchError;
					},
				) => AdditionalEventAttributes;
			} = {},
			shouldWrapWithAnalytics = (): boolean => true,
		): UseMutationConfig<T> => {
			if (!shouldWrapWithAnalytics()) {
				return commitArgs;
			}

			startCapturingExtensions(mutationName);
			let activeInteraction: InteractionMetrics | undefined;
			let ufoHoldCleanup: ReturnType<InteractionContextType['hold']>;
			if (sendUFOEvent) {
				activeInteraction = getActiveInteraction();
				ufoHoldCleanup = context?.hold(activeInteraction?.ufoName);
				addUFOCustomData({
					application: transformApplication(application || null),
					edition: transformEdition(edition || null),
				});
			}

			const defaultExperienceArgs = {
				analyticsSource: analyticsSource || 'unknown',
				application: application || null,
				edition: edition || null,
				additionalAttributes,
			};

			return {
				...commitArgs,
				onCompleted: (response, error) => {
					const aggResponse = isAGGResponse(response) ? response : undefined;
					const { traceId, aggError, errorMessage, statusCode } = stopCapturingAndGetMetaData(
						mutationName,
						aggResponse,
						error,
					);
					let action: ExperienceDescription['action'] = 'taskSuccess';
					if (aggError) {
						action = isAbortableStatusCode(statusCode) ? 'taskAbort' : 'taskFail';
					}

					commitArgs.onCompleted?.(response, error);
					maybeSendUFOErrorAndReleaseHold(
						activeInteraction,
						ufoHoldCleanup,
						traceId,
						sendUFOEvent,
						!!aggError,
						statusCode,
						errorMessage,
					);
					const additionalAttributesWithErrorProperties = {
						...additionalAttributes,
						...(action !== 'taskSuccess' && {
							traceId,
							...(errorMessage !== undefined && { errorMessage }),
							...(statusCode !== undefined && { statusCode }),
						}),
					};
					const overrideAttributes = overrideAttributesCallback
						? overrideAttributesCallback(!aggError, additionalAttributesWithErrorProperties, {})
						: {};
					if (task || overrideAttributes?.task) {
						sendExperienceAnalytics({
							...defaultExperienceArgs,
							experience: task || String(overrideAttributes?.task),
							action,
							additionalAttributes: additionalAttributesWithErrorProperties,
							...overrideAttributes,
						});
					}
				},
				onError: (error: Error | FetchError) => {
					const traceId = stopCapturingAndGetTraceId(mutationName);
					const aggError = extractFirstErrorFromRelayError(error);
					const errorMessage = aggError?.message || error?.message;
					const statusCode = isFetchError(error) ? error?.statusCode : aggError?.statusCode;
					const hasError = !!aggError || !!isFetchError(error);
					const action =
						isAbortableError(error) || isAbortableStatusCode(statusCode) ? 'taskAbort' : 'taskFail';

					commitArgs.onError?.(error);
					maybeSendUFOErrorAndReleaseHold(
						activeInteraction,
						ufoHoldCleanup,
						traceId,
						sendUFOEvent,
						hasError,
						statusCode,
						errorMessage,
					);

					const overrideAttributes = overrideAttributesCallback
						? overrideAttributesCallback(false, additionalAttributes, {
								aggError,
								networkError: hasError,
								statusCode,
								rawError: error,
							})
						: {};
					if (task || overrideAttributes?.task) {
						sendExperienceAnalytics({
							...defaultExperienceArgs,
							experience: task || String(overrideAttributes.task),
							action,
							additionalAttributes: {
								...additionalAttributes,
								traceId,
								...(errorMessage !== undefined && { errorMessage }),
								...(statusCode !== undefined && { statusCode }),
							},
							...overrideAttributes,
						});
					}
				},
			};
		},
		[analyticsSource, application, edition, context],
	);

	return {
		enrichCommitWithIssueExperienceMonitoring,
	};
};
